import { useEffect, useState } from "react";
import { Package, AlertTriangle, TrendingDown, Bookmark, Truck, PackageCheck } from "lucide-react";
import { listInventoryItems, type InventoryListItem } from "../../../api/catalog";
import { Button } from "../../primitives/Button";
import { Input } from "../../primitives/Input";
import { cn } from "../../utils/cn";

function getProductIdFromHash(): string | null {
  const hash = window.location.hash;
  const qs = hash.includes("?") ? hash.split("?")[1] : "";
  const params = new URLSearchParams(qs);
  return params.get("product") || null;
}

export function InventoryOverview() {
  const [items, setItems] = useState<InventoryListItem[]>([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, page_size: 50 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "rupture" | "faible" | "ok">("all");
  const [searchAlerts, setSearchAlerts] = useState("");
  const [sortCol, setSortCol] = useState<keyof InventoryListItem>("product_name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const productFilter = getProductIdFromHash();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listInventoryItems({
      page: 1,
      page_size: 100,
    })
      .then((res) => {
        if (!cancelled) {
          let list = res.items;
          if (productFilter) list = list.filter((i) => i.product_id === productFilter);
          setItems(list);
          setMeta(res.meta);
        }
      })
      .catch((err: Error & { status?: number }) => {
        if (!cancelled) {
          setItems([]);
          setError(err?.status === 403 ? "Accès refusé au module Catalogue." : (err?.message || "Impossible de charger l'inventaire."));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [productFilter]);

  const ruptureCount = items.filter((i) => i.stock_status === "rupture").length;
  const faibleCount = items.filter((i) => i.stock_status === "faible").length;
  const totalStock = items.reduce((s, i) => s + i.stock_quantity, 0);
  const totalReserved = items.reduce((s, i) => s + i.reserved_quantity, 0);
  const openPOCount = 0; // TODO when PO API exists
  const toReceive = 0; // TODO when PO API exists

  let alertes = items;
  if (filter === "rupture") alertes = items.filter((i) => i.stock_status === "rupture");
  else if (filter === "faible") alertes = items.filter((i) => i.stock_status === "faible");
  else if (filter === "ok") alertes = items.filter((i) => i.stock_status === "ok");

  const filteredAlertes = alertes.filter(
    (i) =>
      !searchAlerts ||
      i.product_name.toLowerCase().includes(searchAlerts.toLowerCase()) ||
      (i.product_code || "").toLowerCase().includes(searchAlerts.toLowerCase())
  );
  const sortedAlertes = [...filteredAlertes].sort((a, b) => {
    const va = a[sortCol];
    const vb = b[sortCol];
    const cmp = typeof va === "number" ? va - (vb as number) : String(va ?? "").localeCompare(String(vb ?? ""));
    return sortDir === "asc" ? cmp : -cmp;
  });

  const th = (key: keyof InventoryListItem, label: string) => (
    <th
      className="text-left p-3 font-medium text-text cursor-pointer hover:bg-surface-muted/70 whitespace-nowrap"
      onClick={() => { setSortCol(key); setSortDir((d) => (sortCol === key ? (d === "asc" ? "desc" : "asc") : "asc")); }}
    >
      {label} {sortCol === key && (sortDir === "asc" ? "↑" : "↓")}
    </th>
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-text">Vue d'ensemble</h1>
        {productFilter && (
          <a href="#/inventories" className="text-sm text-primary hover:underline">
            Voir tout l'inventaire
          </a>
        )}
      </div>

      {error && (
        <div className="rounded-md border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger">
          {error}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
        <KpiCard title="Stock total" value={totalStock} subtitle="unités" icon={Package} />
        <KpiCard title="En rupture" value={ruptureCount} subtitle="produits" icon={AlertTriangle} tone="text-danger" />
        <KpiCard title="Stock faible" value={faibleCount} subtitle="produits" icon={TrendingDown} tone="text-warning" />
        <KpiCard title="Réservations actives" value={0} subtitle="(à venir)" icon={Bookmark} />
        <KpiCard title="Commandes ouvertes" value={openPOCount} subtitle="PO" icon={Truck} />
        <KpiCard title="À recevoir" value={toReceive} subtitle="unités" icon={PackageCheck} />
      </div>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <h2 className="text-sm font-semibold text-text">Alertes prioritaires</h2>
          <div className="flex gap-2 flex-wrap items-center">
            <Input
              placeholder="Rechercher (produit, SKU…)"
              value={searchAlerts}
              onChange={(e) => setSearchAlerts(e.target.value)}
              className="max-w-[220px] h-8 text-sm"
            />
            {(["all", "rupture", "faible", "ok"] as const).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={cn(
                  "rounded-md border px-3 py-1.5 text-xs font-medium",
                  filter === f ? "border-primary bg-primary/10 text-primary" : "border-border bg-surface text-muted hover:text-text"
                )}
              >
                {f === "all" ? "Tous" : f === "rupture" ? "Rupture" : f === "faible" ? "Faible" : "OK"}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-muted">Chargement...</p>
        ) : error ? null : (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-surface-muted">
                  {th("product_name", "Produit")}
                  {th("available_quantity", "Disponible")}
                  {th("low_stock_threshold", "Seuil")}
                  {th("reserved_quantity", "Réservé")}
                  <th className="text-right p-3 font-medium text-text">En commande</th>
                  {th("stock_status", "Statut")}
                  <th className="text-right p-3 font-medium text-text">Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedAlertes.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-4 text-center text-muted">
                      {searchAlerts ? "Aucun résultat pour cette recherche." : "Aucune alerte pour ce filtre."}
                    </td>
                  </tr>
                ) : (
                  sortedAlertes.map((row) => (
                    <tr key={row.product_id} className="border-b border-border hover:bg-surface-muted/50">
                      <td className="p-3">
                        <span className="font-medium text-text">{row.product_name}</span>
                        {row.product_code && <span className="text-muted ml-1">({row.product_code})</span>}
                      </td>
                      <td className="text-right p-3">{row.available_quantity}</td>
                      <td className="text-right p-3">{row.low_stock_threshold ?? "—"}</td>
                      <td className="text-right p-3">{row.reserved_quantity}</td>
                      <td className="text-right p-3">—</td>
                      <td className="text-center p-3">
                        <span
                          className={cn(
                            "rounded px-2 py-0.5 text-xs font-medium",
                            row.stock_status === "rupture" && "bg-danger/15 text-danger",
                            row.stock_status === "faible" && "bg-warning/15 text-warning",
                            row.stock_status === "ok" && "bg-success/15 text-success"
                          )}
                        >
                          {row.stock_status === "rupture" ? "Rupture" : row.stock_status === "faible" ? "Faible" : "OK"}
                        </span>
                      </td>
                      <td className="text-right p-3">
                        <a href={`#/inventories/inventaire/${row.product_id}`} className="text-primary hover:underline text-xs">
                          Voir
                        </a>
                        <span className="mx-1 text-muted">|</span>
                        <a href="#/inventories/commandes" className="text-primary hover:underline text-xs">
                          Commander
                        </a>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bloc 3 — Création de rapport */}
      <div className="rounded-md border border-border bg-surface p-4">
        <h2 className="text-sm font-semibold text-text mb-3">Création de rapport</h2>
        <div className="flex flex-wrap gap-3">
          <Button
            intent="default"
            size="sm"
            label="Rapport de statut actuel (PDF ou Excel)"
            onClick={() => {
              /* TODO: ouvrir modal sélection d'articles puis générer PDF/Excel */
              window.alert("Rapport statut actuel (PDF/Excel) — à brancher sur API rapports.");
            }}
          />
          <Button
            intent="default"
            size="sm"
            label="Rapport des commandes en cours"
            onClick={() => {
              /* TODO: rapport commandes en cours */
              window.alert("Rapport des commandes en cours — à brancher sur API commandes.");
            }}
          />
        </div>
        <p className="text-xs text-muted mt-2">
          Rapport statut : sélection d'articles puis export PDF ou Excel. Rapport commandes : liste et statut des commandes ouvertes.
        </p>
      </div>
    </div>
  );
}

function KpiCard({
  title,
  value,
  subtitle,
  icon: Icon,
  tone = "text-text",
}: {
  title: string;
  value: number | string;
  subtitle: string;
  icon: typeof Package;
  tone?: string;
}) {
  return (
    <div className="rounded-md border border-border bg-surface p-4">
      <p className="text-xs text-muted">{title}</p>
      <p className={cn("mt-1 text-xl font-semibold", tone)}>{value}</p>
      {subtitle && <p className="text-xs text-muted">{subtitle}</p>}
      <div className="mt-2 flex justify-end">
        <Icon size={18} className="text-muted" />
      </div>
    </div>
  );
}
